import { Globe, User, Plus, Minus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminData } from '../AdminLoader';
import { TableSection, LoadingState, EmptyState, UserRow } from './TableComponents';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface AdminRowProps {
  admin: AdminData;
  getUserName: (admin: AdminData) => string;
  handleModifyRole?: (registrantId: string, newRole: string) => void;
}

interface MemberRowProps {
  admin: AdminData;
  getUserName: (admin: AdminData) => string;
  handleModifyRole?: (registrantId: string, newRole: string) => void;
  adminIds: Set<string>;
}

export const EntityAdminRow = ({ admin, getUserName, handleModifyRole }: AdminRowProps) => (
  <UserRow
    admin={admin}
    getUserName={getUserName}
    icon={
      <span
        className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: 'var(--anl-accent)' }}
      >
        <Globe className="h-3 w-3 text-white" />
      </span>
    }
    action={
      handleModifyRole && (
        <button
          title="Make Member"
          className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-border)]"
          onClick={() => handleModifyRole(admin.registrantid, 'Member')}
        >
          <Minus size={16} />
        </button>
      )
    }
  />
);

export const EntityMemberRow = ({
  admin,
  getUserName,
  handleModifyRole,
  adminIds,
}: MemberRowProps) => (
  <UserRow
    admin={admin}
    getUserName={getUserName}
    icon={<User className="mr-2 h-4 w-4 text-[var(--anl-text-muted)]" />}
    action={
      handleModifyRole &&
      !adminIds.has(admin.registrantid) && (
        <button
          title="Make Admin"
          className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-border)] hover:text-[var(--anl-text)]"
          onClick={() => handleModifyRole(admin.registrantid, 'Admin')}
        >
          <Plus size={16} />
        </button>
      )
    }
  />
);

interface EntityAdminsTableProps {
  isLoading: boolean;
  admins: AdminData[];
  getUserName: (admin: AdminData) => string;
  handleModifyRole?: (registrantId: string, newRole: string) => void;
}

export const EntityAdminsTable = ({
  isLoading,
  admins,
  getUserName,
  handleModifyRole,
}: EntityAdminsTableProps) => {
  const isMobile = useIsMobile();
  return (
    <div className={`border-[var(--anl-border)] ${isMobile ? 'w-full border-b' : 'w-1/2 border-r'}`}>
      <TableSection title="Admins">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <LoadingState />
          ) : admins.length === 0 ? (
            <EmptyState message="No admins found" />
          ) : (
            <div className="divide-y divide-[var(--anl-border)]">
              {admins.map(admin => (
                <EntityAdminRow
                  key={admin.registrantid}
                  admin={admin}
                  getUserName={getUserName}
                  handleModifyRole={handleModifyRole}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </TableSection>
    </div>
  );
};

interface EntityMembersTableProps {
  isLoading: boolean;
  members: AdminData[];
  getUserName: (admin: AdminData) => string;
  handleModifyRole?: (registrantId: string, newRole: string) => void;
  adminIds: Set<string>;
}

export const EntityMembersTable = ({
  isLoading,
  members,
  getUserName,
  handleModifyRole,
  adminIds,
}: EntityMembersTableProps) => {
  const isMobile = useIsMobile();
  return (
    <div className={isMobile ? 'w-full' : 'w-1/2'}>
      <TableSection title="Members">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <LoadingState />
          ) : members.length === 0 ? (
            <EmptyState message="No members found" />
          ) : (
            <div className="divide-y divide-[var(--anl-border)]">
              {members.map(member => (
                <EntityMemberRow
                  key={member.registrantid}
                  admin={member}
                  getUserName={getUserName}
                  handleModifyRole={handleModifyRole}
                  adminIds={adminIds}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </TableSection>
    </div>
  );
};

interface SplitEntityTablesProps {
  isLoading: boolean;
  admins: AdminData[];
  members: AdminData[];
  getUserName: (admin: AdminData) => string;
  handleModifyRole?: (registrantId: string, newRole: string) => void;
}

export const SplitEntityTables = ({
  isLoading,
  admins,
  members,
  getUserName,
  handleModifyRole,
}: SplitEntityTablesProps) => {
  const isMobile = useIsMobile();
  const adminIds = new Set(admins.map(admin => admin.registrantid));

  return (
    <motion.div
      className="overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'}`}>
        <EntityAdminsTable
          isLoading={isLoading}
          admins={admins}
          getUserName={getUserName}
          handleModifyRole={handleModifyRole}
        />
        <EntityMembersTable
          isLoading={isLoading}
          members={members}
          getUserName={getUserName}
          handleModifyRole={handleModifyRole}
          adminIds={adminIds}
        />
      </div>
    </motion.div>
  );
};
